import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success("Message sent. We'll respond personally.");
      setForm({ full_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left: details + map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <p className="eyebrow mb-6">Visit The Studio</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-[#2D2926] mb-10">
            We're tucked into a leafy
            <br />
            corner of <em className="italic text-[#C89F95]">Daisy Hill</em>.
          </h2>

          <div className="grid sm:grid-cols-2 gap-8 mb-10">
            <Detail icon={MapPin} label="Studio">
              <p className="font-serif text-lg text-[#2D2926] leading-snug">
                75 Daisy Hill Rd
                <br />
                Daisy Hill QLD 4127
              </p>
              <a
                href="https://maps.google.com/?q=75+Daisy+Hill+Rd+Daisy+Hill+QLD+4127"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-link"
                className="hover-line text-xs uppercase tracking-[0.22em] text-[#C89F95] inline-block mt-3"
              >
                Open in Maps →
              </a>
            </Detail>
            <Detail icon={Clock} label="Hours">
              <p className="font-serif text-lg text-[#2D2926] leading-snug">
                Tue – Sat · 9am – 5pm
              </p>
              <p className="text-xs text-[#5C5753] mt-2">By appointment only</p>
            </Detail>
            <Detail icon={Phone} label="Direct">
              <a
                href="tel:0459830505"
                data-testid="contact-phone"
                className="font-serif text-lg text-[#2D2926] hover-line"
              >
                0459 830 505
              </a>
            </Detail>
            <Detail icon={Mail} label="Nurse Team">
              <a
                href="mailto:info@dermaaestheticstudio.com"
                data-testid="contact-email"
                className="font-serif text-lg text-[#2D2926] hover-line break-all"
              >
                info@dermaaestheticstudio.com
              </a>
            </Detail>
          </div>

          <div className="aspect-[16/8] overflow-hidden rounded-sm border border-[#E8DFD8]">
            <iframe
              title="Derma Aesthetic Studio Map"
              src="https://www.google.com/maps?q=75+Daisy+Hill+Rd+Daisy+Hill+QLD+4127&output=embed"
              className="w-full h-full grayscale-[20%]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>

        {/* Right: contact form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <div className="bg-[#F5EFEB] border border-[#E8DFD8] p-8 md:p-10 rounded-sm">
            <p className="eyebrow mb-6">Send a Message</p>
            <h3 className="font-serif text-3xl text-[#2D2926] mb-8 leading-tight">
              A quiet conversation,
              <br />
              not a sales call.
            </h3>

            <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
              <CField label="Name" required>
                <input
                  data-testid="contact-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] transition-colors"
                />
              </CField>
              <CField label="Email" required>
                <input
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] transition-colors"
                />
              </CField>
              <CField label="Phone (optional)">
                <input
                  type="tel"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] transition-colors"
                />
              </CField>
              <CField label="Message" required>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={4}
                  className="bg-transparent border-b border-[#E8DFD8] focus:border-[#C89F95] py-2 outline-none w-full text-[#2D2926] resize-none transition-colors"
                />
              </CField>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="bg-[#2D2926] hover:bg-[#C89F95] disabled:opacity-60 text-white px-8 py-4 rounded-sm text-xs uppercase tracking-[0.24em] transition-colors flex items-center gap-3"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Sending
                  </>
                ) : (
                  "Send Message"
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <Icon size={18} strokeWidth={1.25} className="text-[#C89F95] mt-1 shrink-0" />
      <div>
        <p className="eyebrow mb-2">{label}</p>
        {children}
      </div>
    </div>
  );
}

function CField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-[#5C5753] mb-2">
        {label}
        {required && <span className="text-[#C89F95]"> *</span>}
      </span>
      {children}
    </label>
  );
}
